// PaymentBlock — блок «Платежи» сущности (V12 U-35, волна R67; V15-c — кнопки статуса).
// Список платежей по entityType+entityId (≤5 строк) + кнопки «+ Платёж» (manual dialog
// с preset привязки) и «Загрузить чек» (ReceiptUploadDialog, preset); для PENDING —
// действия «Подтвердить»/«Отклонить» (канон PaymentsListPage.tsx:138-150,336-358).
// Порядок: GET /api/payments?entityType=&entityId=&page=0&size=5 → таблица;
// «Показать все →» — переход на /payments?entityType=&entityId= (фильтр списка).
// SSOT U-35: чек нигде не хранится — после успеха показываем только платёж (source=QR).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { paymentsApi, type Payment, type PaymentEntityType, type PaymentStatus } from '@/api/payments';
import { AmountChip } from '@/pages/crm/components/AmountChip';
import { PaymentNewDialog } from '@/pages/payments/components/PaymentNewDialog';
import { ReceiptUploadDialog } from '@/pages/payments/components/ReceiptUploadDialog';
import { PaymentStatusChip } from '@/pages/payments/components/PaymentStatusChip';
import type { PaymentBinding } from '@/pages/payments/components/EntityBindingSelect';
import { formatDate, formatPaymentSource } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Сколько строк платежей показываем в блоке (канон §3.7: ≤5 + «Показать все»). */
const BLOCK_PAGE_SIZE = 5;

/** Сколько миллисекунд показывать success-бейдж «Платёж зафиксирован». */
const SUCCESS_ALERT_MS = 4000;

interface PaymentBlockProps {
  /** Тип сущности-привязки (COMPANY|CONTACT|DEAL|LEAD). */
  entityType: PaymentEntityType;
  /** Id сущности. */
  entityId: string;
}

/** Блок «Платежи» карточки сущности: таблица до 5 строк + загрузка чека + ручной платёж. */
export function PaymentBlock({ entityType, entityId }: PaymentBlockProps): JSX.Element {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  const binding: PaymentBinding = { entityType, entityId };

  const query = useQuery({
    queryKey: ['payments', entityType, entityId],
    queryFn: () => paymentsApi.list({ entityType, entityId, page: 0, size: BLOCK_PAGE_SIZE }),
    enabled: Boolean(entityId),
  });

  const queryClient = useQueryClient();
  const [statusError, setStatusError] = useState<string | null>(null);

  // Канон мутации статуса — PaymentsListPage.tsx:138-150 (V15-c §11.3; OQ-2 §11.6:
  // общий хук usePaymentStatusMutation — при 3-м использовании; сейчас осознанная копия 12 строк).
  const statusMutation = useMutation({
    mutationFn: ({ payment, next }: { payment: Payment; next: PaymentStatus }) =>
      paymentsApi.update(payment.id, { status: next, expectedUpdatedAt: payment.updatedAt }),
    onSuccess: () => {
      setStatusError(null);
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error) => {
      // 403 (нет доступа) / 409 (stale, счёт аннулирован): Alert + refetch — юзер не в тупике повторных ошибок.
      setStatusError(extractApiErrorMessage(error, t('payments.update.error')));
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });

  // Авто-скрытие success-бейджа (U-35: платёж зафиксирован — QR).
  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(false), SUCCESS_ALERT_MS);
    return () => clearTimeout(timer);
  }, [success]);

  /** Успех загрузки чека: бейдж «Платёж зафиксирован (QR)» (invalidate — в ReceiptUploadDialog). */
  const handleUploaded = (): void => {
    setSuccess(true);
  };

  /** 422-чек → «Создать вручную»: закрываем загрузку, открываем manual с preset. */
  const handleCreateManual = (): void => {
    setUploadOpen(false);
    setManualOpen(true);
  };

  return (
    <section aria-label={t('payments.block.title')}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('payments.block.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setManualOpen(true)}>
            {t('payments.block.add')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setUploadOpen(true)}>
            {t('payments.upload')}
          </Button>
        </Stack>
      </Stack>

      {success ? <Alert severity="success" sx={{ mb: 2 }}>{t('payments.upload.success')}</Alert> : null}
      {statusError ? <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert> : null}

      {query.isLoading ? (
        <Typography variant="body2" color="text.secondary">
          {t('payments.loading')}
        </Typography>
      ) : query.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {query.error instanceof Error ? query.error.message : t('payments.loadError')}
        </Alert>
      ) : !query.data || query.data.content.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('payments.empty')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label={t('payments.block.title')}>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>{t('payments.paymentDate')}</TableCell>
                <TableCell>{t('payments.amount')}</TableCell>
                <TableCell>{t('payments.source')}</TableCell>
                <TableCell>{t('payments.fiscalDocNumber')}</TableCell>
                <TableCell>{t('payments.status')}</TableCell>
                <TableCell align="right">{t('payments.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(query.data.content as Payment[]).map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>#{payment.paymentNumber ?? '—'}</TableCell>
                  <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                  <TableCell>
                    <AmountChip amount={payment.amount} currency={payment.currency} />
                  </TableCell>
                  <TableCell>{formatPaymentSource(payment.source, t)}</TableCell>
                  <TableCell>{payment.fiscalDocNumber ?? '—'}</TableCell>
                  <TableCell>
                    <PaymentStatusChip status={payment.status} />
                  </TableCell>
                  <TableCell align="right">
                    {payment.status === 'PENDING' ? (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t('payments.confirm')}>
                          <IconButton
                            size="small"
                            aria-label={t('payments.confirm')}
                            disabled={statusMutation.isPending}
                            onClick={() => statusMutation.mutate({ payment, next: 'CONFIRMED' })}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('payments.reject')}>
                          <IconButton
                            size="small"
                            aria-label={t('payments.reject')}
                            disabled={statusMutation.isPending}
                            onClick={() => statusMutation.mutate({ payment, next: 'FAILED' })}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {query.data && query.data.total > BLOCK_PAGE_SIZE ? (
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() => navigate(`/payments?entityType=${entityType}&entityId=${entityId}`)}
        >
          {t('payments.block.showAll')}
        </Button>
      ) : null}

      <ReceiptUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} preset={binding} onUploaded={handleUploaded} onCreateManual={handleCreateManual} />
      <PaymentNewDialog open={manualOpen} onClose={() => setManualOpen(false)} preset={binding} />
    </section>
  );
}
